import { Fragment, type ReactNode } from 'react';

import type { CMSHeroSlide } from '@/lib/cms/types';

const MD_BREAKPOINT = '(min-width: 768px)';

/**
 * Pick the viewport-appropriate image URL and focal point for a hero slide.
 * Uses `window.matchMedia` so it must only be called from event handlers or effects.
 */
export function getSlideMedia(slide: CMSHeroSlide) {
  const isDesktop = window.matchMedia(MD_BREAKPOINT).matches;
  const source = isDesktop ? slide.media?.desktop : (slide.media?.mobile ?? slide.media?.desktop);
  const imageUrl = source?.url;
  const focalPoint = source?.focalPoint ?? null;
  const imageWidth = source?.width ?? null;
  const imageHeight = source?.height ?? null;
  const objectPosition = focalPoint ? `${focalPoint.x}% ${focalPoint.y}%` : 'center';
  return { imageUrl, objectPosition, focalPoint, imageWidth, imageHeight };
}

/**
 * Compute a rotated thumbnail order so the active slide is always last in the list.
 * The slides before it fill the visible area from the left; items after the active
 * (if any) overflow and are clipped on the right.
 * E.g. for slides [0,1,2,3,4] with activeIndex=2 → [3,4,0,1,2]
 */
export const getRotatedOrder = (slideCount: number, activeIndex: number): number[] => {
  const order: number[] = [];
  for (let i = 1; i <= slideCount; i++) {
    order.push((activeIndex + i) % slideCount);
  }
  return order;
};

/**
 * Generate extra slide indices that appear AFTER the active thumbnail (at the
 * trailing edge of the strip, hidden in overflow) to prevent re-entering
 * thumbnails from becoming visible during expand/collapse animations.
 *
 * On large desktop ~5 thumbnails are visible. When thumbnails exit on the left,
 * remaining ones slide left and overflow items become visible. Without padding,
 * re-entering thumbnails can briefly appear at the strip's trailing edge.
 *
 * Returns indices that cycle through the slides starting after the active,
 * producing enough items so the strip never looks sparse.
 */
const MIN_TOTAL_THUMBNAILS = 10;

export const getPaddingSlides = (slideCount: number, activeIndex: number): number[] => {
  const paddingNeeded = MIN_TOTAL_THUMBNAILS - slideCount;
  if (paddingNeeded <= 0) return [];

  const indices: number[] = [];
  for (let i = 1; i <= paddingNeeded; i++) {
    indices.push((activeIndex + i) % slideCount);
  }
  return indices;
};

export const parseLineBreaks = (text: string): ReactNode => {
  const parts = text.split(/<br\s*\/?>/i);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {part}
    </Fragment>
  ));
};
